<template>
  <dh-header>
    Profile
    <template #buttons>
      <dh-button variant="none" class="text-gray-500" @click="logout">Logout</dh-button>
      <dh-button icon="add" variant="none" class="text-yellow-300" route="tenant.create">Add Tenant</dh-button>
    </template>
  </dh-header>
  <div class="space-y-8">
    <dh-table :rows="tenants" :loading="loading">
      <dh-table-column label="Name" value="name"></dh-table-column>
      <template v-slot:edit="{ row }">
        <div v-if="tenants.length > 1">
          <p class="pb-2">You may switch tenants to adjust the data you see in the admin.</p>
          <dh-button class="w-32" @click="(event) => handleSwitch(row.id, event)" :loading="isSwitching">Switch</dh-button>
          <hr class="my-4">
        </div>
        <form class="space-y-4" @submit="(e) => saveTenant(e, row.id)">
          <dh-input label="Name" name="name" v-model="row.name" required></dh-input>
          <dh-input label="Phone" name="phoneNumber" v-model="row.phoneNumber" required></dh-input>
          <dh-input label="Email" name="contactEmail" v-model="row.contactEmail" required></dh-input>
          <dh-button variant="action" :loading="saving">Save</dh-button>
        </form>
      </template>
    </dh-table>
  </div>
</template>

<script lang="ts">
import {defineComponent, inject, reactive, ref} from "vue";
import { useRouter } from "vue-router";
import DhHeader from "@/components/DhHeader.vue";
import DhButton from "@/components/DhButton.vue";
import {useRemoteData} from "@/hooks/useRemoteData";
import {useRemoteList} from "@/hooks/useRemoteList";
import {Beacon, beacons, Tenant, tenants as tenantApi} from "@/api";
import DhInput from "@/components/DhInput.vue";
import {useRemoteMutation} from "@/hooks/useRemoteMutation";
import {useExtendedMouseEvent} from "@/hooks/useExtendedMouseEvent";
import DhTable from "@/components/DhTable.vue";
import DhTableColumn from "@/components/DhTableColumn.vue";
import eventemitter from "eventemitter3";

export default defineComponent({
  name: "Profile",
  components: {
    DhInput,
    DhButton,
    DhHeader,
    DhTable,
    DhTableColumn,
  },
  setup() {
    const router = useRouter();
    const saving = ref(false);
    const [tenants, pagination, loading] = useRemoteList(tenantApi.list);
    const newTenant = reactive({name: '', phoneNumber: ''});
    const emitter = inject("eventemitter") as eventemitter;

    const saveTenant = async (event: MouseEvent, id: string) => {
      event.preventDefault();
      const tenant = tenants.value.find(t => t.id === id);
      if (!tenant) {
        return;
      }

      const body: Partial<Tenant> = {
        id: tenant.id,
        name: tenant.name,
        phoneNumber: tenant.phoneNumber,
        contactEmail: tenant.contactEmail,
      };
      saving.value = true;
      await tenantApi.upsert(body);
      saving.value = false;
    };

    const logout = () => {
      localStorage.removeItem("token");
      router.push({ name: "login" });
    };

    const saveNewTenant = (event: MouseEvent) => {
      event.preventDefault();
      tenantApi.upsert(newTenant);
    };

    const isSwitching = ref(false);
    const handleSwitch = async (tenantID: string, event: MouseEvent) => {
      event.preventDefault();
      isSwitching.value = true;
      await tenantApi.switch(tenantID);
      emitter.emit("tenant:update");
      isSwitching.value = false;
    };

    return {
      tenants,
      saving,
      newTenant,
      saveTenant,
      saveNewTenant,
      logout,
      loading,
      handleSwitch,
      isSwitching,
    };
  },
});
</script>
